import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, ComposedChart, Area, Line } from 'recharts';
import { TrendingUp, DollarSign, Clock, Percent, Sparkles, ArrowRight, Home, Banknote } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROIAnalysis = ({ squareFootage, totalCost, bedrooms, bathrooms, isPremiumRegion, propertyValue, renderMode = 'full' }) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!totalCost || totalCost <= 0) return;
    setLoading(true);
    axios.post(`${API}/roi/analysis`, {
      square_footage: squareFootage, total_cost: totalCost,
      bedrooms, bathrooms, property_value: propertyValue || null,
      region: isPremiumRegion ? 'norcal' : 'socal'
    }).then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [totalCost, squareFootage, bedrooms, bathrooms, isPremiumRegion, propertyValue]);

  if (loading) return (
    <div className="py-8 text-center"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-gray-500">{isZh ? '正在生成投资回报分析...' : 'Generating ROI analysis...'}</p></div>
  );
  if (!data) return null;

  const pieData = [
    { name: isZh ? '建造成本' : 'Cost', value: data.cost },
    { name: isZh ? '增值收益' : 'Gain', value: data.equity_gain }
  ];
  const rentalData = data.rental_data.map(d => ({ ...d, name: d.size }));
  const propBefore = data.property_value_before;
  const propAfter = data.property_value_after;
  const gainPct = data.value_uplift_pct;
  const yearlyData = Array.from({ length: 11 }, (_, i) => ({
    year: `Y${i}`, cost: data.cost, rental: Math.round(data.annual_rent * i),
    value: Math.round(data.equity_gain + data.annual_rent * i)
  }));
  const aiParts = (data.ai_analysis || '').split('---');
  const aiText = isZh ? (aiParts[1]?.trim() || aiParts[0]?.trim()) : aiParts[0]?.trim();

  return (
    <div className="page-break-before" data-testid="roi-analysis">
      {/* ===== TITLE PAGE - Same size as address title ===== */}
      <div className="px-6 md:px-8 pt-8 pb-4 border-t-2 border-red-600">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center print:w-8 print:h-8">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{isZh ? '地产大数据投资回报分析' : 'Real Estate Big Data ROI Analysis'}</h2>
        </div>
        <p className="text-sm text-gray-500 ml-13">{isZh ? '基于您的报价数据智能生成' : 'Intelligently generated from your quote data'}</p>
      </div>

      <div className="px-6 md:px-8 pb-6">
        {/* MAIN CONTENT (skip when renderMode='ai') */}
        {renderMode !== 'ai' && <>
        {/* HERO METRICS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          {[
            { icon: DollarSign, label: isZh ? '增值收益' : 'Equity Gain', value: `$${data.equity_gain.toLocaleString()}`, sub: `+${gainPct}%`, color: 'from-red-600 to-red-500' },
            { icon: Banknote, label: isZh ? '月租金' : 'Monthly Rent', value: `$${data.rent_mid.toLocaleString()}`, sub: `$${data.rent_low}-$${data.rent_high}`, color: 'from-green-600 to-green-500' },
            { icon: Clock, label: isZh ? '回本周期' : 'Payback', value: `${data.payback_years_low}-${data.payback_years_high}`, sub: isZh ? '年' : 'years', color: 'from-blue-600 to-blue-500' },
            { icon: Percent, label: isZh ? '收益率' : 'Yield', value: `${data.gross_yield}%`, sub: isZh ? '年化租金收益' : 'annual', color: 'from-amber-500 to-amber-400' }
          ].map((m, i) => (
            <div key={i} className={`bg-gradient-to-br ${m.color} rounded-lg p-3 text-white relative overflow-hidden`}>
              <m.icon className="w-6 h-6 opacity-20 absolute top-2 right-2" />
              <p className="text-xs opacity-80">{m.label}</p>
              <p className="text-xl font-black">{m.value}</p>
              <p className="text-xs opacity-70">{m.sub}</p>
            </div>
          ))}
        </div>

        {/* PROPERTY VALUE COMPARISON */}
        <div className="mb-4 bg-gray-50 rounded-lg p-3">
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Home className="w-3 h-3 text-red-600" />{isZh ? '房产价值对比' : 'Property Value Comparison'}
          </h3>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{isZh ? '建造前' : 'Before ADU'}</span>
                <span className="font-bold">${propBefore.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gray-400 rounded-full" style={{ width: `${(propBefore / propAfter * 100).toFixed(0)}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{isZh ? '建造后' : 'After ADU'}</span>
                <span className="font-bold text-red-600">${propAfter.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <ArrowRight className="w-3 h-3 text-green-600" />
              <span className="text-base font-black text-green-600">+${(propAfter - propBefore).toLocaleString()}</span>
              <span className="text-xs text-gray-500">({((propAfter - propBefore) / propBefore * 100).toFixed(1)}%)</span>
            </div>
          </div>
        </div>

        {/* TWO CHARTS SIDE BY SIDE - Compact */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Pie Chart - Fixed label */}
          <div className="bg-white border border-gray-100 rounded-lg p-3">
            <h3 className="text-xs font-bold text-gray-700 mb-2">{isZh ? '投入 vs 增值' : 'Cost vs Gain'}</h3>
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={4} dataKey="value">
                  <Cell fill="#dc2626" /><Cell fill="#16a34a" />
                </Pie>
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                <Legend formatter={(v) => v} wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart */}
          <div className="bg-white border border-gray-100 rounded-lg p-3">
            <h3 className="text-xs font-bold text-gray-700 mb-2">{isZh ? '各面积租金区间（$/月）' : 'Rent by Size ($/mo)'}</h3>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={rentalData} barGap={1}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                <Bar dataKey="low" fill="#fca5a5" name={isZh ? '低' : 'Low'} radius={[3, 3, 0, 0]} />
                <Bar dataKey="high" fill="#dc2626" name={isZh ? '高' : 'High'} radius={[3, 3, 0, 0]} />
                <Legend wrapperStyle={{ fontSize: 9 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 10-YEAR CHART - Contained */}
        <div className="mb-4 bg-white border border-gray-100 rounded-lg p-3">
          <h3 className="text-xs font-bold text-gray-700 mb-2">{isZh ? '10年累计收益对比' : '10-Year Cumulative Return'}</h3>
          <ResponsiveContainer width="100%" height={150}>
            <ComposedChart data={yearlyData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="year" tick={{ fontSize: 9 }} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} width={45} />
              <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
              <Area type="monotone" dataKey="value" fill="#dcfce7" stroke="#16a34a" strokeWidth={1.5} name={isZh ? '总收益' : 'Total'} />
              <Line type="monotone" dataKey="cost" stroke="#dc2626" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name={isZh ? '成本' : 'Cost'} />
              <Line type="monotone" dataKey="rental" stroke="#2563eb" strokeWidth={1.5} dot={false} name={isZh ? '租金' : 'Rent'} />
              <Legend wrapperStyle={{ fontSize: 9 }} />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 text-center">{isZh ? '绿色区域 = 增值收益 + 累计租金' : 'Green = Equity gain + Cumulative rent'}</p>
        </div>

        {/* KEY METRICS */}
        <div className="mb-4">
          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">{isZh ? '关键指标' : 'Key Metrics'}</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">{isZh ? '建造成本' : 'Cost'}</p>
              <p className="text-lg font-black text-gray-900">${data.cost.toLocaleString()}</p>
              <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-red-500 to-green-500 rounded-full" style={{ width: `${Math.min(((data.equity_gain + data.annual_rent) / data.cost * 100), 100)}%` }} />
              </div>
              <p className="text-xs text-green-600 font-bold mt-1">{isZh ? '产出' : 'Returns'} ${(data.equity_gain + data.annual_rent).toLocaleString()}</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">{isZh ? '年租金' : 'Annual Rent'}</p>
              <p className="text-lg font-black text-gray-900">${data.annual_rent.toLocaleString()}</p>
              <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(data.gross_yield * 5, 100)}%` }} />
              </div>
              <p className="text-xs text-blue-600 font-bold mt-1">{isZh ? '收益率' : 'Yield'}: {data.gross_yield}%</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">{isZh ? '回本' : 'Payback'}</p>
              <p className="text-lg font-black text-gray-900">{data.payback_years_low}-{data.payback_years_high} <span className="text-sm font-normal">{isZh ? '年' : 'yr'}</span></p>
              <div className="mt-1 flex gap-0.5">
                {Array.from({ length: 10 }, (_, i) => (
                  <div key={i} className={`flex-1 h-1.5 rounded-sm ${i < Math.ceil(data.payback_years_high) ? 'bg-amber-500' : 'bg-gray-100'}`} />
                ))}
              </div>
              <p className="text-xs text-amber-600 font-bold mt-1">{isZh ? '增值' : 'Uplift'}: {data.value_uplift_pct}%</p>
            </div>
          </div>
        </div>
        </>}

        {/* AI ANALYSIS — renders in 'ai' or 'full' mode only */}
        {renderMode !== 'main' && aiText && (
          <div className="mb-3 bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-4 text-white">
            <h3 className="text-xs font-bold mb-2 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-amber-400" />
              {isZh ? '大数据投资分析' : 'Big Data Analysis'}
            </h3>
            <div className="text-xs text-gray-300 leading-relaxed whitespace-pre-line">{aiText}</div>
          </div>
        )}

        {renderMode !== 'main' && (
          <p className="text-xs text-gray-400 italic">
            {isZh ? '* 估算值，仅供参考。实际收益因市场条件而异。' : '* Estimates only. Actual returns vary by market conditions.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default ROIAnalysis;
